import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { useLanguage } from '../../context/LanguageContext';
import { createArticleSchema, createBreadcrumbSchema } from '../../utils/structuredData';
import { getPostBySlug } from '../../data/blogPosts';
import './Blog.css';

const BlogPost = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const post = getPostBySlug(slug, lang);
  const baseUrl = 'https://sentmo.com';

  if (!post) {
    return <Navigate to={`/${lang}/blog`} replace />;
  }

  const alternateUrls = [];
  if (post.translations?.de) {
    alternateUrls.push({ lang: 'de', url: `${baseUrl}/de/blog/${post.translations.de}` });
  }
  if (post.translations?.en) {
    alternateUrls.push({ lang: 'en', url: `${baseUrl}/en/blog/${post.translations.en}` });
  }
  if (post.translations?.de) {
    alternateUrls.push({ lang: 'x-default', url: `${baseUrl}/de/blog/${post.translations.de}` });
  }

  const structuredData = [
    createArticleSchema({
      title: post.title,
      description: post.description,
      url: `${baseUrl}/${lang}/blog/${post.slug}`,
      datePublished: post.datePublished,
      dateModified: post.dateModified,
      lang
    }),
    createBreadcrumbSchema([
      { name: t.breadcrumbs.home, url: `${baseUrl}/${lang}` },
      { name: t.breadcrumbs.blog, url: `${baseUrl}/${lang}/blog` },
      { name: post.title, url: `${baseUrl}/${lang}/blog/${post.slug}` }
    ])
  ];

  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(<h2 key={index}>{trimmedLine.replace('## ', '')}</h2>);
      } else if (trimmedLine.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(<h3 key={index}>{trimmedLine.replace('### ', '')}</h3>);
      } else if (trimmedLine.startsWith('- ')) {
        currentList.push(<li key={index}>{trimmedLine.replace('- ', '')}</li>);
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(<p key={index}><strong>{trimmedLine.replace(/\*\*/g, '')}</strong></p>);
      } else if (trimmedLine.match(/^\d+\./)) {
        currentList.push(<li key={index}>{trimmedLine.replace(/^\d+\.\s*/, '')}</li>);
      } else if (trimmedLine !== '') {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        const formattedLine = trimmedLine.split(/(\*\*.*?\*\*)/).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
          }
          return part;
        });
        elements.push(<p key={index}>{formattedLine}</p>);
      }
    });

    if (currentList.length > 0) {
      elements.push(<ul key="final-list">{currentList}</ul>);
    }

    return elements;
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        keywords={`${post.category}, Trading, Sentiment Analyse, ${post.title}`}
        canonicalUrl={`${baseUrl}/${lang}/blog/${post.slug}`}
        lang={lang}
        alternateUrls={alternateUrls}
        ogType="article"
        structuredData={structuredData}
      />
      <article className="page blog-post-page">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to={`/${lang}`}>{t.breadcrumbs.home}</Link>
          <span>/</span>
          <Link to={`/${lang}/blog`}>{t.breadcrumbs.blog}</Link>
          <span>/</span>
          <span>{post.title}</span>
        </nav>

        <header className="post-header">
          <span className="blog-category">{post.category}</span>
          <h1>{post.title}</h1>
          <div className="post-meta">
            <time dateTime={post.datePublished}>
              {t.blog.published}: {post.datePublished}
            </time>
            <span>{post.readTime} {t.blog.readTime}</span>
          </div>
        </header>

        <div className="post-content">
          {renderContent(post.content)}
        </div>

        <footer className="post-footer">
          <div className="post-cta">
            <h3>{t.blog.cta.button}</h3>
            <p>{t.blog.cta.description}</p>
            <Link to={`/${lang}/sentiment`} className="cta-button">{t.blog.cta.button}</Link>
          </div>
          <Link to={`/${lang}/blog`} className="back-link">{t.blog.backToBlog}</Link>
        </footer>
      </article>
    </>
  );
};

export default BlogPost;
